import { useState, useSyncExternalStore } from "react";
import { createRecoveryBackup } from "@fc/core";
import { useAuth } from "../../lib/auth.js";
import { useT } from "../../lib/i18n.js";
import { trpcClient, errorMessage } from "../../lib/trpc.js";
import { isUnlocked, subscribeKeyring } from "../../lib/keyring.js";
import { downloadRecoveryFile } from "../../lib/recovery.js";
import { EmailRecoverySection } from "./EmailRecoverySection.js";
import { PhotoRecovery } from "./PhotoRecovery.js";
import { Button } from "../../components/ui/Button.js";
import { Input } from "../../components/ui/Input.js";
import { Field } from "../../components/ui/Field.js";
import { RecoveryForm } from "../auth/RecoverPage.js";
import "./RecoverySection.css";

export function RecoverySection() {
  const t = useT();
  const { user } = useAuth();
  const unlocked = useSyncExternalStore(subscribeKeyring, isUnlocked, () => false);
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [device, setDevice] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save() {
    if (!user) return;
    setBusy(true); setError(null);
    try {
      const bundle = await trpcClient.auth.keyBundle.mutate({ currentPassword: password });
      if (!bundle) throw new Error(t("Sign in again to create your encryption key first."));
      const backup = await createRecoveryBackup({ userId: user.id, username: user.username }, bundle, password);
      // The secret code is deliberately excluded from the API input.
      await trpcClient.recovery.saveBackup.mutate({ password, publicKey: bundle.publicKey, wrappedPrivateKey: backup.wrappedPrivateKey, salt: backup.salt, totpCode: totpCode || undefined });
      downloadRecoveryFile(`myfriends-recovery-code-${user.username}.json`, backup);
      setSaved(true); setPassword(""); setTotpCode("");
    } catch (caught) { setError(errorMessage(caught)); }
    finally { setBusy(false); }
  }
  return <section className="fc-account__section">
    <h2>{t("Account recovery")}</h2>
    <p className="fc-account__hint">{t("Download a recovery code before you forget your password. Keep the file somewhere private, such as your password manager. Anyone with this file can recover your account; two-factor authentication is still required when enabled.")}</p>
    <p className="fc-account__hint">{t("The code is generated on your device. We store only an encrypted key backup, never the code. Creating another code replaces the previous one.")}</p>
    <form className="fc-recovery__form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
      <Field label={t("Current password")} required><Input type="password" value={password} autoComplete="current-password" disabled={busy} onChange={(e) => setPassword(e.target.value)} required /></Field>
      {user?.totpEnabled && <Field label={t("Authenticator code")} required><Input value={totpCode} inputMode="numeric" autoComplete="one-time-code" maxLength={6} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))} required /></Field>}
      {error && <p role="alert" className="fc-auth__error">{error}</p>}
      <Button type="submit" variant="primary" loading={busy}>{t("Generate and download recovery code")}</Button>
      {saved && <p role="status">{t("Recovery file downloaded. Move it to a safe place. We cannot show this code again.")}</p>}
    </form>
    {unlocked && <><Button variant="secondary" onClick={() => setDevice(!device)}>{t("Use this unlocked device")}</Button>{device && <RecoveryForm mode="device" />}</>}
    <EmailRecoverySection />
    {unlocked && <PhotoRecovery />}
  </section>;
}
